import React, { useState, ChangeEvent } from 'react';
import { connect } from 'react-redux';
import { getSystemsAdmins } from '../../../actionCreators/admin';
import { findAccess, history } from '../../../utils';
import { VALID_EMAIL_REGEX } from '../../../util/regExp';
import {
  ADMIN_PERMISSIONS,
  ADMIN_FLAG_TO_ROLE_NAME,
  READ_ONLY_ROLE_NAME,
} from '../../../constants';
import { PERMISSION_FIELDS } from '../../../util/adminPermissions';
import {
  MovaticFieldGroupCheck,
  MovaticFieldGroupText,
} from '../../../components/Redesing/movatic-field-group';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PageContainer from '../../../components/Redesing/page-container';
import CardView from '../../../components/Redesing/card-view';
import { Stack } from '@mui/system';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import Typography from '@mui/material/Typography';
import CustomToast from '../../../components/Redesing/custom-toast';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import { useSystemStore } from '../../../store/systemStore';
import { inviteAdminToSystemMutation } from '../../../hooks/admins/mutation/V2';

interface IInitialState {
  firstName: string;
  firstNameError: string | null;
  firstNameHelp: string | null;
  lastName: string;
  lastNameError: string | null;
  lastNameHelp: string | null;
  email: string;
  emailHelp: string | null;
  emailError: string | null;
  access: number;
  error: string | null;
  admin: boolean;
  rental: boolean;
  rates: boolean;
  refunds: boolean;
  physical: boolean;
  bank: boolean;
  users: boolean;
  locations: boolean;
  support: boolean;
  maintenance: boolean;
  memberships: boolean;
  buttonDisabled: boolean;
  pending_user_emails: boolean;
  error_report_emails: boolean;
  adminExists: boolean;
}

const AddAdmin: React.FC<any> = (props) => {
  const systemGid = useSystemStore((s) => s.systemInfo.gid);
  const [state, setState] = useState<IInitialState>({
    firstName: '',
    firstNameError: null,
    firstNameHelp: null,
    lastName: '',
    lastNameError: null,
    lastNameHelp: null,
    email: '',
    emailHelp: null,
    emailError: null,
    access: 1,
    error: null,
    admin: false,
    rental: false,
    rates: false,
    refunds: false,
    physical: false,
    bank: false,
    users: false,
    locations: false,
    support: false,
    maintenance: false,
    memberships: false,
    buttonDisabled: false,
    pending_user_emails: true,
    error_report_emails: true,
    adminExists: false,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

    if (e.target.name !== 'admin' || !value) {
      setState({
        ...state,
        admin: false,
        [e.target.name]: value,
        [`${e.target.name}Error`]: null,
      });
    } else if (e.target.name === 'admin' && value) {
      setState({
        ...state,
        admin: true,
        rental: true,
        rates: true,
        bank: true,
        refunds: true,
        physical: true,
        users: true,
        locations: true,
        support: true,
        maintenance: true,
        memberships: true,
        [`${e.target.name}Error`]: null,
      });
    }
  };

  const canSubmit = () => {
    if (state.firstName.length <= 0) {
      setState({
        ...state,
        firstNameHelp: 'First name is required',
        firstNameError: 'error',
      });
      return false;
    }
    setState({ ...state, firstNameHelp: null, firstNameError: null });

    if (state.lastName.length <= 0) {
      setState({
        ...state,
        lastNameHelp: 'Last name is required',
        lastNameError: 'error',
      });
      return false;
    }
    setState({ ...state, lastNameHelp: null, lastNameError: null });

    if (state.email.length <= 0) {
      setState({ ...state, emailHelp: 'Email is required', emailError: 'error' });
      return false;
    }
    setState({ ...state, emailHelp: null, emailError: null });

    // checking if email address is valid
    if (!VALID_EMAIL_REGEX.test(state.email)) {
      setState({
        ...state,
        emailHelp: 'Enter a valid email address',
        emailError: 'error',
      });
      return false;
    }
    setState({ ...state, emailHelp: null, emailError: null });

    return true;
  };

  // Adds the admin through ant's `inviteAdminToSystem`, in one request: it creates
  // the account when the email is new (ant emails the set-password link) or links an
  // existing one, writes the system access and the email-notification flags, and
  // grants a system role per selected permission flag plus the baseline "Read Only"
  // role. The legacy REST `POST /adminuser` is not called, so the Python
  // set-password email is not sent.
  const save = async () => {
    if (!canSubmit()) {
      CustomToast({
        type: 'error',
        message: 'There was an error, please check your form and try again',
      });
      return;
    }

    if (!systemGid) {
      CustomToast({
        type: 'error',
        message: 'The system is still loading, please try again in a moment',
      });
      return;
    }

    setState({
      ...state,
      buttonDisabled: true,
    });
    let access = 1;
    if (state.admin) access += ADMIN_PERMISSIONS['admin'];
    if (state.physical) access += ADMIN_PERMISSIONS['physical'];
    if (state.rates) access += ADMIN_PERMISSIONS['rates'];
    if (state.refunds) access += ADMIN_PERMISSIONS['refunds'];
    if (state.bank) access += ADMIN_PERMISSIONS['bank'];
    if (state.rental) access += ADMIN_PERMISSIONS['rental'];
    if (state.users) access += ADMIN_PERMISSIONS['users'];
    if (state.locations) access += ADMIN_PERMISSIONS['locations'];
    if (state.support) access += ADMIN_PERMISSIONS['support'];
    if (state.maintenance) access += ADMIN_PERMISSIONS['maintenance'];
    if (state.memberships) access += ADMIN_PERMISSIONS['memberships'];

    const roleNames = findAccess(access)
      .map((flag: string) => (ADMIN_FLAG_TO_ROLE_NAME as Record<string, string>)[flag])
      .filter(Boolean);

    // Every new admin gets the baseline "Read Only" role alongside the roles derived
    // from the selected permission flags.
    if (!roleNames.includes(READ_ONLY_ROLE_NAME)) {
      roleNames.push(READ_ONLY_ROLE_NAME);
    }

    const email = state.email.toLowerCase().trim();

    // CustomAsyncToast rejects when the work does, so a failure lands in the catch.
    const invited = await CustomAsyncToast({
      promise: () =>
        inviteAdminToSystemMutation({
          email,
          systemId: systemGid,
          access,
          firstName: state.firstName,
          lastName: state.lastName,
          pendingUserEmails: state.pending_user_emails,
          errorReportEmails: state.error_report_emails,
          roleNames,
        }).then((invite) => {
          if (!invite.isSuccess) {
            throw new Error(invite.errors[0]?.message || 'Adding the admin failed');
          }
          return invite.data;
        }),
      successMessage: () => 'Admin was added',
      loadingMessage: 'Adding admin...',
      errorMessage: 'There was an issue while adding this admin, please try again',
    }).catch(() => null);

    if (invited) {
      props.repullAdmins(props.auth, props.systemId);
      history.push(`/settings/admin/${email}`);
    } else {
      setState((current) => ({ ...current, buttonDisabled: false }));
    }
  };

  const content = () => {
    const {
      firstName,
      lastName,
      email,
      pending_user_emails,
      error_report_emails,
      firstNameHelp,
      lastNameHelp,
      emailHelp,
      firstNameError,
      lastNameError,
      emailError,
    } = state;
    return (
      <div>
        <Typography variant="h6">Admin Information</Typography>
        <MovaticFieldGroupText
          id="firstName"
          name="firstName"
          label="First Name"
          description="Admin's first name"
          hasError={firstNameError === 'error'}
          errorMessage={firstNameHelp}
          value={firstName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setState({ ...state, firstName: e.target.value });
          }}
        />

        <MovaticFieldGroupText
          id="lastName"
          name="lastName"
          label="Last Name"
          description="Admin's last name"
          hasError={lastNameError === 'error'}
          errorMessage={lastNameHelp}
          value={lastName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setState({ ...state, lastName: e.target.value });
          }}
        />

        <MovaticFieldGroupText
          id="email"
          name="email"
          label="Email"
          description="Admin's email address"
          hasError={emailError === 'error'}
          errorMessage={emailHelp}
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setState({ ...state, email: e.target.value });
          }}
        />
        <Typography variant="h6">Admin Permissions</Typography>

        {PERMISSION_FIELDS.map((field) => (
          <MovaticFieldGroupCheck
            key={field.name}
            id={field.name}
            name={field.name}
            label={field.label}
            description={field.description}
            onChange={handleChange}
            checked={state[field.name]}
          />
        ))}

        <Typography variant="h6">Subscribe to Email Notifications</Typography>

        <MovaticFieldGroupCheck
          label="Pending Members"
          description="Recieve notifications for new pending members"
          id="bank"
          name="bank"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setState({ ...state, pending_user_emails: e.target.checked })
          }
          checked={pending_user_emails}
        />
        <MovaticFieldGroupCheck
          label="Support Tickets Reports"
          description="Receive notifications for new support tickets reports submitted by users"
          id="bank"
          name="bank"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setState({ ...state, error_report_emails: e.target.checked })
          }
          checked={error_report_emails}
        />
      </div>
    );
  };

  const button = () => {
    return (
      <IconButtonMenu
        buttons={[
          {
            label: 'Cancel',
            onClick: () => history.push('/settings/admin'),
            startIcon: <CloseIcon />,
          },
          {
            label: 'Save',
            onClick: save,
            startIcon: <SaveIcon />,
            disabled:
              !findAccess(props.systemAccess).includes('admin') && !props.movaticAccess,
          },
        ]}
      />
    );
  };

  return (
    <PageContainer isLoading={!props.systemLoaded}>
      <CardView
        content={
          <Stack paddingX={2} paddingBottom={2}>
            {content()}
          </Stack>
        }
        title="Create Admin"
        headerActions={button()}
      />
      <MovaticCustomModal
        open={state.adminExists}
        onClose={() => setState({ ...state, adminExists: false })}
        content="An admin with this email address already exists. They have been added to this system with their existing first and last name."
        title="Admin Exists"
        action={
          <IconButtonMenu
            buttons={[
              {
                label: 'Continue',
                onClick: () => setState({ ...state, adminExists: false }),
                startIcon: <SaveIcon />,
              },
            ]}
          />
        }
      />
    </PageContainer>
  );
};

export default connect(
  (state: any) => ({
    privateSystem: state.system.current.privateSystem,
    auth: state.admin.admin,
    systemId: state.system.current.id,
    movaticAccess: state.admin.admin.admin.movaticAccess,
    systemAccess: state.admin.systemAccess,
    systemLoaded: state.system.isLoaded,
  }),
  (dispatch: Function) => ({
    repullAdmins: (authInfo: {}, systemId: string) => {
      dispatch(getSystemsAdmins());
    },
  })
)(AddAdmin);
